import React, { useState, useEffect } from "react"
import {
  auth,
  GoogleAuthProvider,
  GithubAuthProvider,
  firestore,
} from "../firebase"

import { telegram } from "../utils/telegram"

const AuthContext = React.createContext()

export function useAuth() {
  return React.useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [images, setImages] = useState(null)
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const [hasMoreImages, setHasMoreImages] = useState(false)
  const [isLoadingMoreImages, setIsLoadingMoreImages] = useState(false)
  const [lastImageDoc, setLastImageDoc] = useState(null)

  async function configUser(user) {
    // const configRef = firestore.collection("config").doc("default")
    // const configDoc = await configRef.get()

    // let free_balance = 8

    // if (configDoc.exists) {
    //   const configData = configDoc.data()
    //   free_balance =
    //     configData.freeBalance !== undefined
    //       ? configData.freeBalance
    //       : free_balance
    // }

    const userRef = firestore.collection("users").doc(user.uid)

    const createdAt = new Date()
    const referrer = localStorage.getItem("referrer")
    const searchKeyword = localStorage.getItem("search_keyword")
    const firstLandingPage = localStorage.getItem("first_landing_page") || "/"

    //const group = Math.random() < 0.5 ? "A" : "C"
    const group = "A"

    const userData = {
      id: user.uid,
      email: user.email,
      //balance: group === "C" ? 20 : 0,
      balance:
        firstLandingPage === "/tools/high-resolution-image-converter" ? 30 : 0,
      createdAt: createdAt,
      referrer: referrer,
      firstLandingPage: firstLandingPage,
      plan: "free",
      group: group,
    }

    if (searchKeyword) {
      userData.search_keyword = searchKeyword
    }

    await userRef.set(userData)
    return userData
  }

  async function signupWithEmail(email, password) {
    try {
      const userCredential = await auth.createUserWithEmailAndPassword(
        email,
        password
      )
      const user = userCredential.user
      await configUser(user)
      return "success"
    } catch (error) {
      telegram(
        "frontend, AuthContext, signupWithEmail, error: " + error.message
      )
      throw new Error("Failed to create an account")
    }
  }

  // Redirect only works when the app is served from the same domain as the
  // Firebase authDomain (clarityai.co). On localhost / preview hosts the
  // redirect result lives in clarityai.co's storage partition and can't be
  // read back, so we fall back to a popup there.
  function shouldUsePopup() {
    if (typeof window === "undefined") return false
    return !window.location.hostname.endsWith("clarityai.co")
  }

  async function handleAuthResult(result) {
    if (!result?.user) {
      return
    }

    const providerId = result.additionalUserInfo?.providerId
    if (providerId === "google.com") {
      localStorage.setItem("lastAuthMethod", "google")
    } else if (providerId === "github.com") {
      localStorage.setItem("lastAuthMethod", "github")
    }

    if (result.additionalUserInfo?.isNewUser) {
      try {
        await configUser(result.user)
      } catch (error) {
        telegram(
          "frontend, AuthContext, handleAuthResult configUser, error: " +
            error.message
        )
      }
    }
  }

  async function signupWithProvider(provider) {
    if (shouldUsePopup()) {
      const result = await auth.signInWithPopup(provider)
      await handleAuthResult(result)
      return
    }
    await auth.signInWithRedirect(provider)
  }

  async function signupWithGoogle() {
    await signupWithProvider(new GoogleAuthProvider())
  }

  async function signupWithGithub() {
    await signupWithProvider(new GithubAuthProvider())
  }

  function login(email, password) {
    try {
      //telegram("User login: " + email)
      return auth.signInWithEmailAndPassword(email, password)
    } catch (error) {
      //telegram("frontend, AuthContext, login, error: " + error.message)
    }
  }

  const logout = async () => {
    await auth.signOut()
    setCurrentUser(null)
    setUser(null)
    setImages(null)
  }

  function resetPassword(email) {
    return auth.sendPasswordResetEmail(email)
  }

  // Load more images (pagination)
  const loadMoreImages = async () => {
    if (
      !currentUser ||
      !lastImageDoc ||
      isLoadingMoreImages ||
      !hasMoreImages
    ) {
      return
    }

    setIsLoadingMoreImages(true)

    try {
      // API images are excluded server-side; requires the composite index
      // images(source ASC, createdAt DESC)
      const moreImagesQuery = firestore
        .collection("users")
        .doc(currentUser.uid)
        .collection("images")
        .where("source", "==", "frontend")
        .orderBy("createdAt", "desc")
        .startAfter(lastImageDoc)
        .limit(100)

      const querySnapshot = await moreImagesQuery.get()

      if (!querySnapshot.empty) {
        const moreImagesData = []
        querySnapshot.forEach((doc) => {
          const imageData = doc.data()
          const imageId = doc.id
          moreImagesData.push({ id: imageId, ...imageData })
        })

        console.log(
          "Loaded more images from Firestore:",
          moreImagesData.length
        )

        // Append new images to existing ones
        setImages((prevImages) => [...prevImages, ...moreImagesData])

        // Update pagination state
        const hasMore = querySnapshot.docs.length >= 100
        setHasMoreImages(hasMore)
        setLastImageDoc(querySnapshot.docs[querySnapshot.docs.length - 1])
      } else {
        setHasMoreImages(false)
      }
    } catch (error) {
      console.error("Error loading more images:", error)
    } finally {
      setIsLoadingMoreImages(false)
    }
  }

  // Handle the return from a Google/GitHub redirect sign-in. Runs once on
  // mount, regardless of which page initiated the sign-in, so that brand new
  // users always get their Firestore document created.
  useEffect(() => {
    auth
      .getRedirectResult()
      .then((result) => handleAuthResult(result))
      .catch(() => {})
  }, [])

  useEffect(() => {
    let unsubscribeAuth = null
    let unsubscribeUser = null
    let unsubscribeImages = null

    const setupListeners = async () => {
      // Auth listener
      unsubscribeAuth = auth.onAuthStateChanged((user) => {
        setCurrentUser(user)
        setLoading(false)
      })

      if (currentUser) {
        // User data listener
        const usersRef = firestore.collection("users").doc(currentUser.uid)
        unsubscribeUser = usersRef.onSnapshot(
          async (snapshot) => {
            if (snapshot.exists) {
              const userData = snapshot.data()
              const userId = snapshot.id

              // webhook_stripe.py copies this onto the Stripe customer as
              // `referral` metadata, which is how Rewardful attributes a sale.
              window.rewardful?.("ready", () => {
                const referral = window.Rewardful?.referral
                if (referral && userData.rewardful_referral !== referral) {
                  usersRef
                    .update({ rewardful_referral: referral })
                    .catch(() => {})
                }
              })

              try {
                const paymentsRef = usersRef.collection("payments")
                const collectionsPayments = await paymentsRef.get()
                const collectionsData = collectionsPayments.docs.map((doc) =>
                  doc.data()
                )
                const sumOfCredits = collectionsData
                  .filter((item) => item.status === "succeeded")
                  .flatMap((item) => item.items)
                  .map((item) => parseInt(item.price.metadata.credits))
                  .filter((credits) => !isNaN(credits))
                  .reduce((accumulator, current) => accumulator + current, 0)

                setUser({
                  id: userId,
                  ...userData,
                  credits_bought: sumOfCredits,
                })
              } catch (error) {
                // Silently handle permissions errors for payments
                if (error.code === "permission-denied") {
                  // Set user data without credits for blocked users
                  setUser({ id: userId, ...userData, credits_bought: 0 })
                  return
                }
                console.error("Error fetching payments:", error)
              }

              // Setup images listener with filtering for specific users
              // Remove old listener if exists
              if (unsubscribeImages) {
                unsubscribeImages()
              }

              // API images are excluded server-side; requires the composite
              // index images(source ASC, createdAt DESC)
              const imagesQuery = firestore
                .collection("users")
                .doc(currentUser.uid)
                .collection("images")
                .where("source", "==", "frontend")
                .orderBy("createdAt", "desc")
                .limit(100)

              unsubscribeImages = imagesQuery.onSnapshot(
                async (querySnapshot) => {
                  const imagesData = []
                  querySnapshot.forEach((doc) => {
                    const imageData = doc.data()
                    const imageId = doc.id
                    imagesData.push({ id: imageId, ...imageData })
                  })

                  console.log(
                    "Loaded images from Firestore:",
                    imagesData.length
                  )
                  setImages(imagesData)

                  // Check if there might be more images
                  const hasMore = querySnapshot.docs.length >= 100
                  setHasMoreImages(hasMore)

                  // Save last document for pagination
                  if (querySnapshot.docs.length > 0) {
                    setLastImageDoc(
                      querySnapshot.docs[querySnapshot.docs.length - 1]
                    )
                  }
                },
                (error) => {
                  // Silently handle permissions errors - user doesn't have access
                  if (error.code === "permission-denied") {
                    // Don't log this error as it's expected for blocked users
                    setImages([])
                    return
                  }
                  console.error("Error fetching images:", error)
                }
              )
            }
          },
          (error) => {
            // Silently handle permissions errors - user doesn't have access
            if (error.code === "permission-denied") {
              // Don't log this error as it's expected for blocked users
              setUser(null)
              return
            }
            console.error("Error fetching user data:", error)
          }
        )
      }
    }

    setupListeners()

    return () => {
      if (unsubscribeAuth) unsubscribeAuth()
      if (unsubscribeUser) unsubscribeUser()
      if (unsubscribeImages) unsubscribeImages()
    }
  }, [currentUser])

  const value = {
    currentUser,
    signupWithGoogle,
    signupWithGithub,
    signupWithEmail,
    login,
    logout,
    resetPassword,
    user,
    images,
    showLoginPopup,
    setShowLoginPopup,
    hasMoreImages,
    isLoadingMoreImages,
    loadMoreImages,
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
